import { SEO } from '@/components/SEO';

export function PrivacyPage() {
  return (
    <article className="max-w-3xl mx-auto px-4 pt-12 pb-20 sm:pt-16 aces-prose">
      <SEO
        title="Privacy Policy — ACES"
        description="How ACES handles your data: no accounts, no personal details collected, and how saved progress, fonts, videos and advertising cookies work."
        canonical="https://acescommerce.com/privacy/"
      />
      <h1>Privacy Policy</h1>
      <p><em>Last updated: 1 November 2026</em></p>

      <h2>The short version</h2>
      <p>
        ACES is a free public website. You do not need to log in, and we do not ask for your name, email,
        phone number, school, or any other personal details to use it. We plan to show advertising through
        Google AdSense once our application is approved. Until then, the site does not set advertising cookies.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Your progress.</strong> Chapters you have completed and your quiz scores are saved in your
          browser&rsquo;s local storage, on your device. They are not sent to our servers. You can delete them by
          clearing this site&rsquo;s data in your browser settings.
        </li>
        <li>
          <strong>Session activity.</strong> Simulation steps and similar interaction events may be kept in your
          browser&rsquo;s session storage while the tab is open. They are cleared when you close the tab, and they
          are not sent to our servers.
        </li>
        <li>
          <strong>Standard hosting logs.</strong> Our hosting provider, Netlify, processes the basic technical
          details every web server receives (such as IP address and browser type) to deliver pages. We do not use
          these logs to identify individual students.
        </li>
        <li>
          <strong>Fonts and videos.</strong> Fonts are loaded from Google Fonts, so Google receives your IP address
          when a page loads. Chapter videos use YouTube&rsquo;s privacy-enhanced domain (youtube-nocookie.com), which
          stores nothing about you until you press play. After that,{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent-600 underline">Google&rsquo;s privacy policy</a> applies.
        </li>
        <li>
          <strong>Advertising cookies (Google AdSense), once enabled.</strong> Ads are non-personalised by default,
          so they are chosen from the page you are reading rather than your visits to other sites. Google may still
          use cookies for basic functions, such as limiting how often an ad appears.
        </li>
      </ul>

      <h2>How advertising cookies work</h2>
      <p>
        When advertising is enabled, Google and its partners use cookies to show ads. ACES asks for non-personalised
        ads by default, because most students are under 18. Those ads are chosen from the page you are reading, not
        from your visits to other sites. If personalised advertising is ever switched on, we will update this page
        first and explain what changes.
      </p>
      <p>
        You can opt out of personalised advertising by visiting{' '}
        <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-accent-600 underline">Google Ads Settings</a>.
        Alternatively, you can opt out of a third-party vendor&rsquo;s use of cookies for personalised advertising by
        visiting{' '}
        <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-accent-600 underline">www.aboutads.info</a>.
        To read how Google uses information from sites that use its services, see{' '}
        <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-accent-600 underline">Google&rsquo;s partner-sites policy</a>.
      </p>

      <h2>What we do NOT do</h2>
      <ul>
        <li>We do not sell student data.</li>
        <li>
          We do not share student data with third parties, except the service providers described above: hosting,
          fonts, video and, once enabled, advertising.
        </li>
        <li>We do not build profiles of individual students.</li>
        <li>We do not contact parents, schools, or tutors without explicit permission.</li>
        <li>We do not use student responses to train AI models without explicit, opt-in consent (none today).</li>
      </ul>

      <h2>Students under 18</h2>
      <p>
        ACES is built for students in Class 11 and Class 12, most of whom are between 15 and 18. India&rsquo;s
        Digital Personal Data Protection Act, 2023 treats anyone under 18 as a child, so we apply extra care to every
        student who uses the site:
      </p>
      <ul>
        <li>We do not ask for accounts, names, dates of birth, school names or phone numbers.</li>
        <li>We do not track children or build advertising profiles of them.</li>
        <li>
          Ads are non-personalised by default, so they do not follow students from one site to another. If that
          ever changes, this page will say so before it does.
        </li>
        <li>
          A parent or guardian can ask what we hold about a child, or ask us to delete it, by emailing us. We will
          act on that request.
        </li>
      </ul>

      <h2>Your choices</h2>
      <ul>
        <li>
          Clear this site&rsquo;s data in your browser settings to remove saved progress and session activity.
        </li>
        <li>
          Once advertising is enabled, opt out of personalised ads using the links above, or block third-party cookies
          in your browser.
        </li>
        <li>Use a browser that blocks third-party trackers. ACES works without them.</li>
      </ul>

      <h2>Contact</h2>
      <p>
        For privacy questions, email{' '}
        <a href="mailto:hello@acescommerce.com" className="text-accent-600 underline">hello@acescommerce.com</a>.
      </p>

      <h2>Updates</h2>
      <p>
        When we make a material change to this policy, we will update the date at the top of this page.
      </p>
    </article>
  );
}
